import { getInjectedProvider } from './injectedProvider';

/*
 * Wallet chain switching, in one place.
 *
 * There were two implementations before this: HyperliquidPanel.switchToHyperEVM
 * handled error 4902 by adding the chain and warned on failure, while
 * gmxUtils.switchToChain did neither — so the GMX "SWITCH TO ARBITRUM" button
 * rejected into nothing and looked inert. Anything that switches chains should
 * behave the same way, so it lives here.
 */
const CHAIN_PARAMS = {
  42161: {
    chainId: '0xa4b1',
    chainName: 'Arbitrum One',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://arb1.arbitrum.io/rpc'],
    blockExplorerUrls: ['https://arbiscan.io'],
  },
  421614: {
    chainId: '0x66eee',
    chainName: 'Arbitrum Sepolia',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://sepolia-rollup.arbitrum.io/rpc'],
    blockExplorerUrls: ['https://sepolia.arbiscan.io'],
  },
  999: {
    chainId: '0x3e7',
    chainName: 'HyperEVM',
    nativeCurrency: { name: 'HYPE', symbol: 'HYPE', decimals: 18 },
    rpcUrls: ['https://rpc.hyperliquid.xyz/evm'],
    blockExplorerUrls: ['https://purrsec.com'],
  },
};

/**
 * Switch the wallet to `chainId`, adding the network first if it isn't known.
 *
 * @returns {Promise<{ok: boolean, reason?: string, error?: Error}>} never throws —
 *   callers are UI handlers, and an unhandled rejection here reads to the user
 *   as a dead button.
 */
export async function switchChain(chainId, rawProvider = null) {
  const p = rawProvider || getInjectedProvider();
  if (!p) return { ok: false, reason: 'no_provider' };

  const hex = '0x' + Number(chainId).toString(16);
  try {
    await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] });
    return { ok: true };
  } catch (e) {
    // 4902: chain unknown to the wallet. Add it, which also switches to it.
    if (e && (e.code === 4902 || e.code === -32603)) {
      const params = CHAIN_PARAMS[Number(chainId)];
      if (!params) return { ok: false, reason: 'unknown_chain', error: e };
      try {
        await p.request({ method: 'wallet_addEthereumChain', params: [params] });
        return { ok: true };
      } catch (addErr) {
        return { ok: false, reason: addErr.code === 4001 ? 'rejected' : 'add_failed', error: addErr };
      }
    }
    if (e && e.code === 4001) return { ok: false, reason: 'rejected', error: e };
    return { ok: false, reason: 'switch_failed', error: e };
  }
}

/** Display name for a chain id, for UI that has to say where the wallet is. */
export function chainName(chainId) {
  if (chainId === null || chainId === undefined) return 'not connected';
  const known = CHAIN_PARAMS[Number(chainId)];
  if (known) return known.chainName;
  return ({ 1: 'Ethereum', 10: 'Optimism', 8453: 'Base', 137: 'Polygon' })[Number(chainId)]
    || `chain ${chainId}`;
}

export { CHAIN_PARAMS };
